import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, Plus, Search, Building2, Eye, Receipt, Send, Loader2,
  Copy, Link2, UserX, Pencil, ShieldCheck,
} from "lucide-react";
import { clientsApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { LoadingState, ErrorState, EmptyState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const EMPTY = {
  name: "", contact_name: "", contact_email: "", contact_phone: "",
  address: "", postcode: "", default_payment_terms_days: "",
};

export default function Clients() {
  const navigate = useNavigate();
  const { beginImpersonation } = useAuth();
  const { data, loading, error, reload } = useAsync(() => clientsApi.list(), []);
  const [q, setQ] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [portalUsers, setPortalUsers] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);

  const list = Array.isArray(data) ? data : [];
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter((c) =>
      `${c.name} ${c.contact_name || ""} ${c.contact_email || ""}`.toLowerCase().includes(s));
  }, [list, q]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadPortalUsers = async (id) => {
    setPortalUsers(null);
    setPortalBusy(true);
    try { setPortalUsers((await clientsApi.listPortalUsers(id)).data?.users || []); }
    catch { setPortalUsers([]); }
    finally { setPortalBusy(false); }
  };

  const openCreate = () => { setEditing(null); setForm(EMPTY); setPortalUsers(null); setDialogOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "", contact_name: c.contact_name || "", contact_email: c.contact_email || "",
      contact_phone: c.contact_phone || "", address: c.address || "", postcode: c.postcode || "",
      default_payment_terms_days: c.default_payment_terms_days ?? "",
    });
    setDialogOpen(true);
    loadPortalUsers(c.id);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Client name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        contact_name: form.contact_name.trim() || undefined,
        contact_email: form.contact_email.trim() || undefined,
        contact_phone: form.contact_phone.trim() || undefined,
        address: form.address.trim() || undefined,
        postcode: form.postcode.trim() || undefined,
      };
      const terms = form.default_payment_terms_days;
      if (terms !== "" && terms != null) payload.default_payment_terms_days = Number(terms);
      else if (editing) payload.default_payment_terms_days = "inherit";
      if (editing) await clientsApi.update(editing.id, payload);
      else await clientsApi.create(payload);
      toast.success(editing ? "Client updated" : "Client created");
      setDialogOpen(false);
      reload();
    } catch (e) { toast.error(apiError(e, "Could not save client")); }
    finally { setSaving(false); }
  };

  const invite = async (c) => {
    setBusyId(`${c.id}-invite`);
    try {
      const r = await clientsApi.invite(c.id);
      toast.success("Portal access enabled");
      if (editing && editing.id === c.id) setEditing({ ...editing, portal_code: r.data.portal_code, portal_enabled: true });
      reload();
    } catch (e) { toast.error(apiError(e, "Could not enable the portal")); }
    finally { setBusyId(null); }
  };

  const preview = async (c) => {
    if (!window.confirm(`View the portal as "${c.name}"?\n\nYou'll be signed in read-only and can leave any time using the "Exit preview" banner.`)) return;
    setBusyId(`${c.id}-preview`);
    try {
      const r = await clientsApi.impersonate(c.id);
      await beginImpersonation(r.data.access_token, c.name);
    } catch (e) { toast.error(apiError(e, "Could not open portal preview")); setBusyId(null); }
  };

  const sendStatement = async (c) => {
    if (!window.confirm(`Email an outstanding-balance statement to ${c.contact_email || "this client"}?`)) return;
    setBusyId(`${c.id}-stmt`);
    try { await clientsApi.sendStatement(c.id); toast.success("Statement emailed"); }
    catch (e) { toast.error(apiError(e, "Could not send statement")); }
    finally { setBusyId(null); }
  };

  const revokeUser = async (userId, label) => {
    if (!window.confirm(`Revoke portal access for ${label}?\n\nThey will no longer be able to sign in.`)) return;
    try {
      await clientsApi.removePortalUser(editing.id, userId);
      setPortalUsers((u) => (u || []).filter((x) => x.id !== userId));
      toast.success("Portal access revoked");
    } catch (e) { toast.error(apiError(e, "Could not revoke access")); }
  };

  const copy = (text, msg) => { try { navigator.clipboard?.writeText(text); toast.success(msg); } catch { /* ignore */ } };

  return (
    <div>
      <PageHeader
        title="Clients"
        subtitle="Manage your clients and their portal access"
        testId="clients-header"
        actions={<Button onClick={openCreate} data-testid="add-client-btn"><Plus className="mr-1.5 h-4 w-4" /> Add client</Button>}
      />

      {loading && <LoadingState label="Loading clients…" />}
      {!loading && error && <ErrorState message={error} onRetry={reload} />}

      {!loading && !error && (
        <>
          <div className="relative mb-4 max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search clients…" className="pl-10" value={q} onChange={(e) => setQ(e.target.value)} data-testid="clients-search" />
          </div>

          {filtered.length === 0 ? (
            <EmptyState
              title={list.length === 0 ? "No clients yet" : "No matches"}
              message={list.length === 0 ? "Add a client, then invite them to their portal to share reports, quotes and invoices." : "Try a different search."}
              testId="clients-empty"
            />
          ) : (
            <div className="space-y-2" data-testid="clients-list">
              {filtered.map((c) => (
                <Card key={c.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`client-row-${c.id}`}>
                  <button className="flex min-w-0 items-center gap-3 text-left" onClick={() => navigate(`/clients/${c.id}`)} data-testid={`client-open-${c.id}`}>
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"><Users className="h-5 w-5" /></div>
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-slate-800">{c.name}</p>
                      <p className="truncate text-sm text-slate-500">
                        {c.contact_name || "—"}{c.contact_email ? ` · ${c.contact_email}` : ""}
                      </p>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        <Badge variant="outline" className="gap-1 text-slate-500"><Building2 className="h-3 w-3" />{c.building_count ?? 0} buildings</Badge>
                        {c.portal_enabled
                          ? <Badge variant="outline" className="gap-1 bg-emerald-50 text-emerald-700"><ShieldCheck className="h-3 w-3" />Portal active</Badge>
                          : <Badge variant="outline" className="text-slate-400">Portal not invited</Badge>}
                      </div>
                    </div>
                  </button>

                  <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`client-edit-${c.id}`}><Pencil className="h-4 w-4" /><span className="ml-1.5 hidden sm:inline">Edit</span></Button>
                    <Button size="sm" variant="outline" onClick={() => navigate(`/invoices?client=${c.id}&name=${encodeURIComponent(c.name)}`)} data-testid={`client-invoices-${c.id}`}><Receipt className="h-4 w-4" /><span className="ml-1.5 hidden sm:inline">Invoices</span></Button>
                    <Button size="sm" variant="outline" onClick={() => sendStatement(c)} disabled={busyId === `${c.id}-stmt`} data-testid={`client-statement-${c.id}`}>
                      {busyId === `${c.id}-stmt` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}<span className="ml-1.5 hidden sm:inline">Statement</span>
                    </Button>
                    {c.portal_enabled
                      ? <Button size="sm" onClick={() => preview(c)} disabled={busyId === `${c.id}-preview`} data-testid={`client-preview-${c.id}`}>
                          {busyId === `${c.id}-preview` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}<span className="ml-1.5 hidden sm:inline">View portal</span>
                        </Button>
                      : <Button size="sm" onClick={() => invite(c)} disabled={busyId === `${c.id}-invite`} data-testid={`client-invite-${c.id}`}>
                          {busyId === `${c.id}-invite` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}<span className="ml-1.5 hidden sm:inline">Invite</span>
                        </Button>}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="client-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit client" : "Add client"}</DialogTitle>
            <DialogDescription>Client details, payment terms and portal access.</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label htmlFor="cl-name">Client name *</Label>
              <Input id="cl-name" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="client-name-input" />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div><Label htmlFor="cl-cn">Contact name</Label><Input id="cl-cn" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid="client-contact-name-input" /></div>
              <div><Label htmlFor="cl-cp">Contact phone</Label><Input id="cl-cp" value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} data-testid="client-contact-phone-input" /></div>
            </div>
            <div><Label htmlFor="cl-ce">Contact email</Label><Input id="cl-ce" type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} data-testid="client-contact-email-input" /></div>
            <div><Label htmlFor="cl-ad">Address</Label><Input id="cl-ad" value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="client-address-input" /></div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div><Label htmlFor="cl-pc">Postcode</Label><Input id="cl-pc" value={form.postcode} onChange={(e) => set("postcode", e.target.value)} data-testid="client-postcode-input" /></div>
              <div><Label htmlFor="cl-pt">Payment terms (days)</Label><Input id="cl-pt" type="number" min="0" placeholder="Company default" value={form.default_payment_terms_days} onChange={(e) => set("default_payment_terms_days", e.target.value)} data-testid="client-terms-input" /></div>
            </div>

            {editing && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="client-portal-section">
                <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-700"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Portal access</p>
                {editing.portal_enabled && editing.portal_code ? (
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <code className="rounded bg-white px-2 py-1 text-sm font-semibold text-slate-800 ring-1 ring-slate-200" data-testid="client-portal-code">{editing.portal_code}</code>
                      <Button size="sm" variant="outline" onClick={() => copy(editing.portal_code, "Portal code copied")} data-testid="client-copy-code"><Copy className="mr-1 h-3.5 w-3.5" />Code</Button>
                      <Button size="sm" variant="outline" onClick={() => copy(`${window.location.origin}/portal/login`, "Portal link copied")} data-testid="client-copy-link"><Link2 className="mr-1 h-3.5 w-3.5" />Login link</Button>
                    </div>
                    <p className="text-xs text-slate-500">Share the code + link so the client can register at <span className="font-medium">/portal/login</span>. The code can be reused by multiple staff at the client.</p>
                  </div>
                ) : (
                  <Button size="sm" onClick={() => invite(editing)} disabled={busyId === `${editing.id}-invite`} data-testid="client-dialog-invite">
                    {busyId === `${editing.id}-invite` ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Link2 className="mr-1.5 h-4 w-4" />}Enable portal & generate code
                  </Button>
                )}

                <div className="mt-3 border-t border-slate-200 pt-3">
                  <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Registered portal users</p>
                  {portalBusy ? (
                    <p className="text-sm text-slate-400">Loading…</p>
                  ) : !portalUsers || portalUsers.length === 0 ? (
                    <p className="text-sm text-slate-400" data-testid="client-portal-users-empty">No one has registered under this client yet.</p>
                  ) : (
                    <div className="space-y-1.5" data-testid="client-portal-users">
                      {portalUsers.map((u) => (
                        <div key={u.id} className="flex items-center justify-between gap-2 rounded-lg bg-white px-3 py-2 ring-1 ring-slate-200">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-slate-700">{u.name || u.email}</p>
                            <p className="truncate text-xs text-slate-400">{u.email}</p>
                          </div>
                          <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => revokeUser(u.id, u.name || u.email)} data-testid={`client-revoke-user-${u.id}`}><UserX className="h-4 w-4" /></Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="client-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="client-save-btn">
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}{editing ? "Save changes" : "Create client"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
